import { useEffect, useRef, useState } from 'react';

// The @cuemath/rest-api GET store is keyed by (request id + query) and only
// swaps the component's `data` over to the new key's resource on the store's
// deferred (setTimeout) notification. So for the render right after a query
// changes, `data` still holds the PREVIOUS query's response — and under a label
// that already reflects the new query, that flashes one query's rows/totals
// under another query's heading (e.g. experiment B's numbers under "A").
//
// This returns `true` only once a response has actually settled for the CURRENT
// queryKey. Consumers gate query-labelled sections on it (rendering the loader
// until it is true), so a section never shows one query's data under another's
// label. A same-key refetch stays `true` throughout, so a background refresh
// keeps the existing rows visible under the overlay spinner rather than blanking
// them. A failure is not treated as settled — the caller's error branch renders
// instead.
export const useSettledKey = (queryKey, isProcessing, error) => {
  const [settledKey, setSettledKey] = useState(null);
  const wasProcessing = useRef(isProcessing);
  useEffect(() => {
    // A fetch just went PENDING -> done for the key that was in flight, which is
    // always the current queryKey (the fetch effect uses it). Mark it settled.
    if (wasProcessing.current && !isProcessing && !error) setSettledKey(queryKey);
    wasProcessing.current = isProcessing;
  }, [isProcessing, error, queryKey]);
  return settledKey === queryKey;
};
